import _typeof from "@babel/runtime/helpers/typeof";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { createEditorSelectionAPI } from '../../../selection-api/api';
import { breakoutPlugin, collabEditPlugin, dataConsumerMarkPlugin, datePlugin, emojiPlugin, extensionPlugin, fragmentMarkPlugin, helpDialogPlugin, insertBlockPlugin, jiraIssuePlugin, layoutPlugin, toolbarListsIndentationPlugin, macroPlugin, maxContentSizePlugin, mediaPlugin, mentionsPlugin, panelPlugin, placeholderTextPlugin, rulePlugin, saveOnEnterPlugin, tasksAndDecisionsPlugin, textColorPlugin, statusPlugin, alignmentPlugin, indentationPlugin, customAutoformatPlugin, feedbackDialogPlugin, historyPlugin, expandPlugin, isExpandInsertionEnabled, scrollIntoViewPlugin, mobileDimensionsPlugin, findReplacePlugin, mobileSelectionPlugin, annotationPlugin, captionPlugin, avatarGroupPlugin, viewUpdateSubscriptionPlugin, beforePrimaryToolbarPlugin, codeBidiWarningPlugin, copyButtonPlugin, borderPlugin } from '../../../plugins';
import { listPlugin } from '@atlaskit/editor-plugin-list';
import { imageUploadPlugin } from '@atlaskit/editor-plugin-image-upload';
import { guidelinePlugin } from '@atlaskit/editor-plugin-guideline';
import { contextPanelPlugin } from '@atlaskit/editor-plugin-context-panel';
import { gridPlugin } from '@atlaskit/editor-plugin-grid';
import { cardPlugin } from '@atlaskit/editor-plugin-card';
import { tablesPlugin } from '@atlaskit/editor-plugin-table';
import { contentInsertionPlugin } from '@atlaskit/editor-plugin-content-insertion';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { isFullPage as fullPageCheck } from '../../../utils/is-full-page';
import { getMediaFeatureFlag } from '@atlaskit/media-common';
import { createDefaultPreset } from './default';
import { shouldForceTracking } from '@atlaskit/editor-common/utils';
/**
 * Creates a preset with all of the available plugins.
 * Basis for create-plugins-list and can be used to migrate from Editor -> EditorNext (Presets project)
 * with minimal friction.
 *
 * @param appearance
 * @param props A subset of full EditorProps for the full feature preset
 * @param featureFlags
 * @param prevAppearance The appearance of the editor in the previous render
 * @returns a full featured preset configured according to the provided props - basis for create-plugins-list
 */
export default function createUniversalPreset(appearance, props, featureFlags, prevAppearance, createAnalyticsEvent) {
  var isMobile = appearance === 'mobile';
  var isComment = appearance === 'comment';
  var isFullPage = fullPageCheck(appearance);
  var getEditorFeatureFlags = function getEditorFeatureFlags() {
    return featureFlags;
  };
  var editorSelectionAPI = createEditorSelectionAPI();
  var defaultPreset = createDefaultPreset(_objectSpread(_objectSpread({}, props), {}, {
    createAnalyticsEvent: createAnalyticsEvent
  }));
  var statusMenuDisabled = !props.allowStatus ? true : _typeof(props.allowStatus) === 'object' ? Boolean(props.allowStatus.menuDisabled) : false;
  var hasBeforePrimaryToolbar = function hasBeforePrimaryToolbar(components) {
    if (components && 'before' in components) {
      return !!components.before;
    }
    return false;
  };
  var finalPreset = defaultPreset.add(dataConsumerMarkPlugin).add(contentInsertionPlugin).maybeAdd(breakoutPlugin, function (plugin, builder) {
    if (props.allowBreakout && isFullPage) {
      return builder.add([plugin, {
        allowBreakoutButton: appearance === 'full-page'
      }]);
    }
    return builder;
  }).maybeAdd(alignmentPlugin, function (plugin, builder) {
    if (props.allowTextAlignment) {
      return builder.add(plugin);
    }
    return builder;
  }).maybeAdd(textColorPlugin, function (plugin, builder) {
    if (props.allowTextColor) {
      return builder.add([plugin, props.allowTextColor]);
    }
    return builder;
  }).add([listPlugin, {
    restartNumberedLists: featureFlags === null || featureFlags === void 0 ? void 0 : featureFlags.restartNumberedLists
  }]).maybeAdd(rulePlugin, function (plugin, builder) {
    if (props.allowRule) {
      return builder.add(plugin);
    }
    return builder;
  }).maybeAdd(expandPlugin, function (plugin, builder) {
    if (props.allowExpand) {
      return builder.add([plugin, {
        allowInsertion: isExpandInsertionEnabled(props),
        useLongPressSelection: false,
        appearance: appearance
      }]);
    }
    return builder;
  }).maybeAdd(guidelinePlugin, function (plugin, builder) {
    if (props.media || props.allowTables) {
      return builder.add(plugin);
    }
    return builder;
  }).maybeAdd(gridPlugin, function (plugin, builder) {
    if (props.media) {
      return builder.add([plugin, {
        shouldCalcBreakoutGridLines: isFullPage
      }]);
    }
    return builder;
  }).maybeAdd(mediaPlugin, function (plugin, builder) {
    if (props.media) {
      var alignLeftOnInsert = typeof props.media.alignLeftOnInsert !== 'undefined' ? props.media.alignLeftOnInsert : isComment;
      var showMediaLayoutOptions = typeof props.media.allowAdvancedToolBarOptions !== 'undefined' ? props.media.allowAdvancedToolBarOptions : isFullPage || isComment;
      return builder.add([plugin, _objectSpread(_objectSpread({}, props.media), {}, {
        allowLazyLoading: !isMobile,
        allowBreakoutSnapPoints: isFullPage,
        allowAdvancedToolBarOptions: showMediaLayoutOptions,
        allowDropzoneDropLine: isFullPage,
        allowMediaSingleEditable: !isMobile,
        allowRemoteDimensionsFetch: !isMobile,
        editorSelectionAPI: editorSelectionAPI,
        // This is a wild one. I didnt quite understand what the code was doing
        // so a bit of guess for now.
        allowMarkingUploadsAsIncomplete: isMobile,
        fullWidthEnabled: appearance === 'full-width',
        uploadErrorHandler: props.uploadErrorHandler,
        waitForMediaUpload: props.waitForMediaUpload,
        isCopyPasteEnabled: !isMobile,
        alignLeftOnInsert: alignLeftOnInsert,
        getEditorFeatureFlags: getEditorFeatureFlags
      })]);
    }
    return builder;
  }).maybeAdd(captionPlugin, function (plugin, builder) {
    // EDM-799: inside caption plugin we do the feature flag in enabling the plugin
    if (props.media && getMediaFeatureFlag('captions', props.media.featureFlags)) {
      return builder.add(plugin);
    }
    return builder;
  }).maybeAdd(mentionsPlugin, function (plugin, builder) {
    if (props.mentionProvider) {
      var _props$mention$insert, _props$mention, _props$mention2;
      return builder.add([plugin, {
        sanitizePrivateContent: props.sanitizePrivateContent,
        insertDisplayName: (_props$mention$insert = (_props$mention = props.mention) === null || _props$mention === void 0 ? void 0 : _props$mention.insertDisplayName) !== null && _props$mention$insert !== void 0 ? _props$mention$insert : props.mentionInsertDisplayName,
        allowZeroWidthSpaceAfter: !isMobile,
        HighlightComponent: (_props$mention2 = props.mention) === null || _props$mention2 === void 0 ? void 0 : _props$mention2.HighlightComponent
      }]);
    }
    return builder;
  }).maybeAdd(emojiPlugin, function (plugin, builder) {
    if (props.emojiProvider) {
      return builder.add(plugin);
    }
    return builder;
  }).maybeAdd(tablesPlugin, function (plugin, builder) {
    if (props.allowTables) {
      var tableOptions = !props.allowTables || typeof props.allowTables === 'boolean' ? {} : props.allowTables;
      return builder.add([plugin, {
        tableOptions: tableOptions,
        // tableResizingEnabled will replace breakoutEnabled once FF is 100% rolled out,
        // logic below is to help codemod during cleanup
        tableResizingEnabled: getBooleanFF('platform.editor.custom-table-width') && ['full-page', 'full-width'].includes(appearance || ''),
        breakoutEnabled: appearance === 'full-page',
        allowContextualMenu: !isMobile,
        fullWidthEnabled: appearance === 'full-width',
        wasFullWidthEnabled: prevAppearance && prevAppearance === 'full-width',
        editorSelectionAPI: editorSelectionAPI,
        getEditorFeatureFlags: getEditorFeatureFlags
      }]);
    }
    return builder;
  }).maybeAdd(tasksAndDecisionsPlugin, function (plugin, builder) {
    if (props.allowTasksAndDecisions || props.taskDecisionProvider) {
      return builder.add([plugin, {
        allowNestedTasks: props.allowNestedTasks,
        consumeTabs: isFullPage,
        useLongPressSelection: false
      }]);
    }
    return builder;
  }).maybeAdd(feedbackDialogPlugin, function (plugin, builder) {
    if (props.feedbackInfo) {
      return builder.add([plugin, props.feedbackInfo]);
    }
    return builder;
  }).maybeAdd(helpDialogPlugin, function (plugin, builder) {
    if (props.allowHelpDialog) {
      return builder.add([plugin, !!props.legacyImageUploadProvider]);
    }
    return builder;
  }).maybeAdd(saveOnEnterPlugin, function (plugin, builder) {
    if (props.saveOnEnter && props.onSave) {
      return builder.add([plugin, props.onSave]);
    }
    return builder;
  }).maybeAdd(imageUploadPlugin, function (plugin, builder) {
    if (props.legacyImageUploadProvider) {
      return builder.add(plugin);
    }
    return builder;
  }).maybeAdd(mediaPlugin, function (plugin, builder) {
    if (props.legacyImageUploadProvider && !props.media) {
      return builder.add([plugin, {
        allowMediaSingle: {
          disableLayout: true
        },
        allowMediaGroup: false,
        isCopyPasteEnabled: true,
        editorSelectionAPI: editorSelectionAPI
      }]);
    }
    return builder;
  }).maybeAdd(collabEditPlugin, function (plugin, builder) {
    if (props.collabEdit || props.collabEditProvider) {
      var _collabEditOptions$EX;
      var collabEditOptions = {
        sanitizePrivateContent: props.sanitizePrivateContent
      };
      if (props.collabEdit) {
        collabEditOptions = _objectSpread(_objectSpread({}, props.collabEdit), collabEditOptions);
      }
      return builder.add([plugin, _objectSpread(_objectSpread({}, collabEditOptions), {}, {
        EXPERIMENTAL_allowInternalErrorAnalytics: (_collabEditOptions$EX = collabEditOptions.EXPERIMENTAL_allowInternalErrorAnalytics) !== null && _collabEditOptions$EX !== void 0 ? _collabEditOptions$EX : shouldForceTracking()
      })]);
    }
    return builder;
  }).maybeAdd(maxContentSizePlugin, function (plugin, builder) {
    if (props.maxContentSize) {
      return builder.add([plugin, props.maxContentSize]);
    }
    return builder;
  }).maybeAdd(jiraIssuePlugin, function (plugin, builder) {
    if (props.allowJiraIssue) {
      return builder.add(plugin);
    }
    return builder;
  }).maybeAdd(panelPlugin, function (plugin, builder) {
    if (props.allowPanel) {
      var allowPanel = _typeof(props.allowPanel) === 'object' ? props.allowPanel : {};
      return builder.add([plugin, {
        useLongPressSelection: false,
        allowCustomPanel: allowPanel.allowCustomPanel,
        allowCustomPanelEdit: allowPanel.allowCustomPanelEdit
      }]);
    }
    return builder;
  }).maybeAdd(contextPanelPlugin, function (plugin, builder) {
    if (isFullPage) {
      return builder.add(contextPanelPlugin);
    }
    return builder;
  }).maybeAdd(extensionPlugin, function (plugin, builder) {
    if (props.allowExtension) {
      var extensionConfig = _typeof(props.allowExtension) === 'object' ? props.allowExtension : {};
      return builder.add([plugin, {
        breakoutEnabled: appearance === 'full-page' && extensionConfig.allowBreakout !== false,
        allowAutoSave: extensionConfig.allowAutoSave,
        extensionHandlers: props.extensionHandlers,
        useLongPressSelection: false,
        appearance: appearance
      }]);
    }
    return builder;
  }).maybeAdd(macroPlugin, function (plugin, builder) {
    if (props.macroProvider) {
      return builder.add(plugin);
    }
    return builder;
  }).maybeAdd(annotationPlugin, function (plugin, builder) {
    // See default list for when adding annotations with a provider
    if (!props.annotationProviders && props.allowConfluenceInlineComment) {
      return builder.add([plugin, undefined]);
    }
    return builder;
  }).maybeAdd(datePlugin, function (plugin, builder) {
    if (props.allowDate) {
      var dateConfig = _typeof(props.allowDate) === 'object' ? props.allowDate : {};
      return builder.add([plugin, {
        weekStartDay: dateConfig.weekStartDay
      }]);
    }
    return builder;
  }).maybeAdd(placeholderTextPlugin, function (plugin, builder) {
    if (props.allowTemplatePlaceholders) {
      var options = props.allowTemplatePlaceholders !== true ? props.allowTemplatePlaceholders : {};
      return builder.add([plugin, options]);
    }
    return builder;
  }).maybeAdd(layoutPlugin, function (plugin, builder) {
    if (props.allowLayouts) {
      var layoutOptions = _typeof(props.allowLayouts) === 'object' ? props.allowLayouts : {};
      return builder.add([plugin, _objectSpread(_objectSpread({}, layoutOptions), {}, {
        useLongPressSelection: false,
        UNSAFE_allowSingleColumnLayout: layoutOptions.UNSAFE_allowSingleColumnLayout
      })]);
    }
    return builder;
  }).maybeAdd(cardPlugin, function (plugin, builder) {
    var _props$linking;
    if ((_props$linking = props.linking) !== null && _props$linking !== void 0 && _props$linking.smartLinks || props.smartLinks || props.UNSAFE_cards) {
      var _props$linking2, _props$linking3;
      var fullWidthMode = appearance === 'full-width';
      return builder.add([plugin, _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, props.UNSAFE_cards), props.smartLinks), (_props$linking2 = props.linking) === null || _props$linking2 === void 0 ? void 0 : _props$linking2.smartLinks), {}, {
        platform: isMobile ? 'mobile' : 'web',
        fullWidthMode: fullWidthMode,
        linkPicker: (_props$linking3 = props.linking) === null || _props$linking3 === void 0 ? void 0 : _props$linking3.linkPicker,
        editorAppearance: appearance
      })]);
    }
    return builder;
  }).maybeAdd(customAutoformatPlugin, function (plugin, builder) {
    if (props.autoformattingProvider) {
      return builder.add(plugin);
    }
    return builder;
  }).maybeAdd(statusPlugin, function (plugin, builder) {
    if (props.allowStatus) {
      return builder.add([plugin, {
        menuDisabled: statusMenuDisabled,
        allowZeroWidthSpaceAfter: !isMobile
      }]);
    }
    return builder;
  }).maybeAdd(indentationPlugin, function (plugin, builder) {
    if (props.allowIndentation) {
      return builder.add(plugin);
    }
    return builder;
  }).maybeAdd(scrollIntoViewPlugin, function (plugin, builder) {
    if (props.autoScrollIntoView !== false) {
      return builder.add(scrollIntoViewPlugin);
    }
    return builder;
  }).maybeAdd(historyPlugin, function (plugin, builder) {
    if (isMobile || props.allowUndoRedoButtons) {
      return builder.add(historyPlugin);
    }
    return builder;
  }).maybeAdd(mobileDimensionsPlugin, function (plugin, builder) {
    if (isMobile) {
      return builder.add(plugin);
    }
    return builder;
  }).maybeAdd(mobileSelectionPlugin, function (plugin, builder) {
    if (isMobile) {
      return builder.add(plugin);
    }
    return builder;
  }).add([toolbarListsIndentationPlugin, {
    showIndentationButtons: !!featureFlags.indentationButtonsInTheToolbar,
    allowHeadingAndParagraphIndentation: !!props.allowIndentation
  }]).add([insertBlockPlugin, {
    allowTables: !!props.allowTables,
    allowExpand: isExpandInsertionEnabled(props),
    insertMenuItems: props.insertMenuItems,
    horizontalRuleEnabled: props.allowRule,
    nativeStatusSupported: !statusMenuDisabled,
    showElementBrowserLink: props.elementBrowser && props.elementBrowser.showModal || false,
    replacePlusMenuWithElementBrowser: props.elementBrowser && props.elementBrowser.replacePlusMenu || false
  }]).maybeAdd(beforePrimaryToolbarPlugin, function (plugin, builder) {
    if (hasBeforePrimaryToolbar(props.primaryToolbarComponents) && !featureFlags.twoLineEditorToolbar) {
      return builder.add([plugin, {
        beforePrimaryToolbarComponents: props.primaryToolbarComponents.before
      }]);
    }
    return builder;
  }).maybeAdd(avatarGroupPlugin, function (plugin, builder) {
    if (featureFlags.showAvatarGroupAsPlugin === true && !featureFlags.twoLineEditorToolbar) {
      return builder.add([plugin, {
        collabEdit: props.collabEdit,
        takeFullWidth: !hasBeforePrimaryToolbar(props.primaryToolbarComponents)
      }]);
    }
    return builder;
  }).maybeAdd(findReplacePlugin, function (plugin, builder) {
    if (props.allowFindReplace) {
      return builder.add([plugin, {
        takeFullWidth: !!featureFlags.showAvatarGroupAsPlugin === false && !hasBeforePrimaryToolbar(props.primaryToolbarComponents),
        twoLineEditorToolbar: !!featureFlags.twoLineEditorToolbar
      }]);
    }
    return builder;
  }).maybeAdd(borderPlugin, function (plugin, builder) {
    if (props.UNSAFE_allowBorderMark) {
      return builder.add(plugin);
    }
    return builder;
  }).maybeAdd(fragmentMarkPlugin, function (plugin, builder) {
    if (props.allowFragmentMark) {
      return builder.add(plugin);
    }
    return builder;
  }).maybeAdd(viewUpdateSubscriptionPlugin, function (plugin, builder) {
    if (featureFlags.enableViewUpdateSubscription) {
      return builder.add(plugin);
    }
    return builder;
  }).add([codeBidiWarningPlugin, {
    appearance: appearance
  }]).maybeAdd(copyButtonPlugin, function (plugin, builder) {
    if (featureFlags.floatingToolbarCopyButton) {
      return builder.add(plugin);
    }
    return builder;
  });
  return finalPreset;
}